/**
 * RecordDetailPage - Full page view for a single record
 *
 * Atomic level: Page
 * Composes: RecordDetail, ConductScoreIndicator, Button
 *
 * Displays comprehensive record details including:
 * - Customer information and verbatim text
 * - Conduct score and labels (dissatisfaction, strain, harm, consumer duty)
 * - Regulatory flags (complaint detection, vulnerability)
 * - Investigation section with outcome simulation (when confirmed as complaint)
 *
 * Following GO UI Design System patterns
 */

import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useNavigation } from '../../../contexts/NavigationContext';
import { useRecord, useConfirmComplaint, useRejectComplaint, useCollection, useReclassifyRecord, useStartSimulation } from '../../../hooks/useRecords';
import { useOrganizationDisplaySettings } from '../../../hooks/useOrganizationDisplaySettings';
import { RecordDetail } from './components';
import { Button } from '../../ui/button';
import { RootCauseStatusBanner } from '../common/components';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '../../ui/breadcrumb';
import { toast } from 'sonner';

interface RecordDetailPageProps {
  className?: string;
  /** Record ID - defaults to route param if not provided */
  recordId?: string;
  /** Enable complaint detection features */
  complaintDetectionEnabled?: boolean;
  /** Enable vulnerability detection features */
  vulnerabilityDetectionEnabled?: boolean;
}

/**
 * RecordDetailPage Component
 *
 * Fetches and displays full record details with navigation and actions
 */
const RecordDetailPage: React.FC<RecordDetailPageProps> = ({
  className,
  recordId: propRecordId,
  complaintDetectionEnabled = true,
  vulnerabilityDetectionEnabled = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateWithReferrer } = useNavigation();
  const { recordId: routeRecordId, collectionId } = useParams<{ recordId: string; collectionId: string }>();
  const recordId = propRecordId || routeRecordId;
  const { data: organizationDisplaySettings } = useOrganizationDisplaySettings();

  // Optimistic override for complaint confirmation. null = defer to server.
  const [confirmationOverride, setConfirmationOverride] = useState<boolean | null>(null);

  // Fetch record data
  const {
    data: record,
    isLoading,
    error,
  } = useRecord(recordId || '', { enabled: !!recordId });

  // Fetch collection data for breadcrumb
  const {
    data: collection,
  } = useCollection(collectionId || '', { enabled: !!collectionId });

  // Mutations
  const confirmComplaintMutation = useConfirmComplaint();
  const rejectComplaintMutation = useRejectComplaint();
  const reclassifyMutation = useReclassifyRecord();
  const startSimulationMutation = useStartSimulation();

  // Reclassify and simulation state
  const [isReclassifying, setIsReclassifying] = useState(false);
  const [isStartingSimulation, setIsStartingSimulation] = useState(false);

  // Handle back navigation
  const handleBack = () => {
    const stateReferrer = (location.state as { referrer?: string } | null)?.referrer;
    if (stateReferrer) {
      navigate(stateReferrer);
      return;
    }
    // If we came from a collection, go back to it
    if (collectionId) {
      navigate(`/v4/records/${collectionId}`);
    } else {
      // Otherwise go to vault home
      navigate('/v4/records');
    }
  };

  // Handle confirm complaint with optimistic UI
  const handleConfirmComplaint = async (recordId: string) => {
    setConfirmationOverride(true);
    try {
      await confirmComplaintMutation.mutateAsync({ recordId });
      toast.success('Acknowledged as complaint - Investigation enabled');
      setConfirmationOverride(null);
    } catch (error) {
      console.error('Failed to confirm complaint:', error);
      toast.error('Failed to confirm complaint. Please try again.');
      setConfirmationOverride(null);
    }
  };

  // Handle reject complaint with optimistic UI
  const handleRejectComplaint = async (recordId: string) => {
    setConfirmationOverride(false);
    try {
      await rejectComplaintMutation.mutateAsync({ recordId });
      toast.success('Marked as not a complaint - Investigation disabled');
      setConfirmationOverride(null);
    } catch (error) {
      console.error('Failed to reject complaint:', error);
      toast.error('Failed to mark as not a complaint. Please try again.');
      setConfirmationOverride(null);
    }
  };

  // Handle reclassify
  const handleReclassify = async (recordId: string) => {
    setIsReclassifying(true);
    try {
      await reclassifyMutation.mutateAsync({ recordId });
      toast.success('Reclassification started', {
        description: 'The record will be updated when processing completes.',
      });
    } catch (error) {
      console.error('Failed to reclassify record:', error);
      toast.error('Reclassification failed', {
        description: 'Please try again later.',
      });
    } finally {
      setIsReclassifying(false);
    }
  };

  // Handle start simulation
  const handleStartSimulation = async (recordId: string) => {
    setIsStartingSimulation(true);
    try {
      const simulation = await startSimulationMutation.mutateAsync({ recordId });
      toast.success('Case guidance started', {
        description: 'Analyzing against historical ombudsman decisions...',
      });
      // Navigate to simulation workspace, preserving V4 route as referrer
      navigateWithReferrer(`/v3/ombudsman/simulations/${simulation.id}`, location.pathname);
    } catch (error) {
      console.error('Failed to start simulation:', error);
      toast.error('Case guidance failed to start', {
        description: 'Please try again later.',
      });
      setIsStartingSimulation(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/70 bg-background/80 px-6 py-20 text-center shadow-[var(--shadow-card)]">
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Loading Record</h2>
          <p className="text-muted-foreground">Fetching record details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !record) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/70 bg-background/80 px-6 py-20 text-center shadow-[var(--shadow-card)]">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Record</h2>
          <p className="text-muted-foreground max-w-md">
            {error instanceof Error ? error.message : 'Record not found or an unexpected error occurred'}
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={handleBack}>
              Go Back
            </Button>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Merge confirmed status
  const displayRecord = {
    ...record,
    isConfirmedComplaint: confirmationOverride ?? record.isConfirmedComplaint,
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
      <div className="space-y-5">
        <RootCauseStatusBanner settings={organizationDisplaySettings} />

        {/* Breadcrumb */}
        <div className="rounded-2xl border border-border/70 bg-background/90 px-4 py-4 shadow-[var(--shadow-card)] sm:px-5">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/v4/records">Collections</BreadcrumbLink>
              </BreadcrumbItem>
              {collection && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href={`/v4/records/${collectionId}`}>
                      {collection.collection.name}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {record.summary.slice(0, 50)}{record.summary.length > 50 ? '...' : ''}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Record Detail Content */}
        <div className="overflow-hidden rounded-2xl border border-border/70 bg-background/90 shadow-[var(--shadow-card)]">
          <RecordDetail
            record={displayRecord}
            complaintDetectionEnabled={complaintDetectionEnabled}
            vulnerabilityDetectionEnabled={vulnerabilityDetectionEnabled}
            organizationDisplaySettings={organizationDisplaySettings}
            onConfirmComplaint={handleConfirmComplaint}
            onRejectComplaint={handleRejectComplaint}
            onReclassify={handleReclassify}
            onStartSimulation={handleStartSimulation}
            isReclassifying={isReclassifying}
            isStartingSimulation={isStartingSimulation}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export { RecordDetailPage };
export default RecordDetailPage;
